import { signup } from "@/store/signup";
import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import { actions as generalActions } from "./actions";
import { activityLog } from "./activity-log/index";
import { alarmMasking } from "./alarm-masking/index";
import { alarmPoints } from "./alarm-points";
import { areaEventHistory } from "./area-event-history";
import { areaNotes } from "./area-notes/index";
import { areas } from "./areas/index";
import { caseManagement } from "./case-management/index";
import { csvFormHandler } from "./csv-form-handler";
import { csvParser } from "./csv-parser";
import { customFields } from "./custom-fields/index";
import { devices } from "./devices/index";
import { distanceConfiguration } from "./distance-configuration";

import { emails } from "./emails/index";
import { eventSearch } from "./event-search/index";
import { eventqueue } from "./eventqueue/index";
import { externalMapLayers } from "./external-map-layers/index";
import { fieldOps } from "./field-ops/index";
import { GenericTable } from "./generic-table/index";
import { getters as generalGetters } from "./getters";
import { guardChat } from "./guard-chat";
import { manualRaise } from "./manual-raise/index";
import { mapLayers } from "./map-layers/index";
import { map } from "./map/index";
import mobileRaise from "./mobile-raise";
import mobileRaiseTemplates from "./mobile-raise-templates";
import { mobile } from "./mobile/index";
import { mutations as generalMutations } from "./mutations";
import { postLoginAppBootstrap } from "./plugins";
import { reports } from "./reports/index";
import { responseActions } from "./response-actions/index";
import { responses } from "./responses/index";
import { rulesEngine } from "./rules-engine";
import { schedules } from "./schedules";
import { sessions } from "./sessions/index";
import { setup } from "./setup/index";
import { siteMonitorCameras } from "./site-monitor-cameras/index";
import { siteMonitor } from "./site-monitor/index";
import { getDefaultState } from "./state";
import { subscription } from "./subscription/index";
import { systemHealth } from "./system-health";
import { tasks } from "./tasks/index";
import { tours } from "./tours/index";
import { userContext } from "./user-context/index";
import { userManagement } from "./user-management/index";
import { views } from "./views/index";
import createMutationsSharer from "./vuex-shared-mutations";

const debug = process.env.NODE_ENV !== "production";

Vue.use(Vuex);

// @todo
// 2. thinking we could have a section of state called `UI` that would represent the state of various UI elements
// (e.g. look at all the state properties that end with `Open`)
// 3. thinking about creating CCure, Groups, Event, EventShare modules

export default new Vuex.Store({
	state: getDefaultState(),
	getters: {
		...generalGetters
	},
	mutations: {
		...generalMutations
	},
	actions: {
		...generalActions
	},
	modules: {
		fieldOps,
		eventqueue,
		siteMonitor,
		tasks,
		userManagement,
		siteMonitorCameras,
		sessions,
		mapLayers,
		map,
		eventSearch,
		devices,
		responses,
		mobile,
		views,
		reports,
		activityLog,
		areas,
		tours,
		areaNotes,
		GenericTable,
		manualRaise,
		mobileRaiseTemplates,
		mobileRaise,
		subscription,
		signup,
		emails,
        externalMapLayers,
        caseManagement,
		csvParser,
		csvFormHandler,
		alarmMasking,
		guardChat,
		userContext,
		setup,
		responseActions,
		systemHealth,
		rulesEngine,
		distanceConfiguration,
		schedules,
		alarmPoints,
		customFields,
		areaEventHistory
	},
	plugins: [
		// @ref https://github.com/robinvdvleuten/vuex-persistedstate
		createPersistedState({
			paths: [
				"User",
				"featuresList",
				"timeZones",
				"fieldOps",
				"eventqueue.alarmQueueFooter",
				"eventqueue.hideMapFlag",
				"siteMonitor.hideMapFlag",
				"activeRegion",
				"siteMonitor.EventDetails",
				"signup",
				"mobile",
				"siteMonitor.deviceServerDetails",
				"siteMonitor.applianceServerDetails",
				"siteMonitor.eventOutcomes",
				"siteMonitorCameras.deviceControllerClips",
				"siteMonitorCameras.layoutIndex",
				"elevationLabels",
				"systemHealth.selectedFilter",
				"mapLayers.advancedFilters",
				"tenantSystemPrefs",
				"customFields",
				"areaEventHistory",
				"autoFeedAlarms",
				"systemPrefs",
			]
		}),
		postLoginAppBootstrap,
		createMutationsSharer({
			predicate: [
				"logout",
				"setFeaturesList",
				"setTenantSystemPrefs",
				"SetTimeZones",
				"setActiveRegion",
				"siteMonitor/setIsСontroller",
				"siteMonitor/setActiveMapItems",
				"siteMonitor/setActiveMapItemsRequired",
				"siteMonitor/setMapCircleCenter",
				"siteMonitor/setEventDetails",
				"siteMonitor/setSelectedEventRecord",
				"siteMonitor/setActivity",
				"siteMonitor/setEventOutcomes",
				"siteMonitorCameras/setRequiresEventDetails",
				"siteMonitorCameras/setAwaitingCamera",
				"siteMonitorCameras/setLinkRecord",
				"siteMonitorCameras/setCameraLink",
				"siteMonitorCameras/setPushContents",
				"siteMonitorCameras/setShownAuditClips",
				"siteMonitorCameras/setDeviceControllerClips",
				"siteMonitorCameras/setLayoutIndex",
				"tours/setAllTours",
				"tours/unsetTour",
				"fieldOps/setViewingAssetId",
				"fieldOps/addOrUpdateActiveSubscriber",
				"fieldOps/cleanUpSubscribers",
				"fieldOps/setTimer",
				"fieldOps/setLastPollCall",
				"mobile/setUserAssetStatus",
				"mobile/setUserAssetMapItem",
				"mobile/setDeviceLocation",
				"mobile/setEventShareItems",
				"mobile/setActiveEventShareItem",
				"mobile/setNavigationMode",
				"mobile/setNavigateToActiveAlarm",
				"mobile/setNavigationLocation",
				"mobile/setFocusedEventID",
				"mobile/setChatTab",
				"mobile/setChatOpen",
				"setElevationLabels",
				"systemHealth/setStatusFilter",
				"mapLayers/setAdvancedFilters",
				"customFields/setAreaCustomFields",
				"customFields/setContactCustomFields",
				"customFields/setAlarmCustomFields",
				"areaEventHistory/setIsPopoutModeEnabled",
				"areaEventHistory/setAreaEventHistory",
				"areaEventHistory/setFilter",
				"areaEventHistory/setTimeZone",
				"areaEventHistory/setTotalRecords",
				"autoFeedAlarms/toggleAutoFeedAlarms"
			],
			sharingKey: "vuex-mutations-sharer",
			storageKey: "vuex-mutation-sharer-storage",
			excludedPages: [
				"eventsearch"
			]
		})
	],
	strict: debug
});
